import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getRoleDisplayName } from '../../utils/roles';

interface UserContextMenuProps {
  userRole: 'admin' | 'member';
}

const UserContextMenu = ({ userRole }: UserContextMenuProps) => {
  const { user, userProfile, signOut } = useAuth();
  const isSuperAdmin = userProfile?.role === 'super_admin';
  
  // Get the actual role display name (e.g., "Group Creator" instead of "Admin")
  const actualRoleName = getRoleDisplayName(userProfile?.role) || (userRole === 'admin' ? 'Admin' : 'Member');
  const [isOpen, setIsOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
    setIsOpen(false);
  };

  const handleLogoutConfirm = async () => {
    try {
      setShowLogoutConfirm(false);
      await signOut();
      // Navigate to login page - the auth state change listener will clear user state
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Still navigate to login even if signOut fails
      setShowLogoutConfirm(false);
      navigate('/login', { replace: true });
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  const menuItems = [
    // Hide "My Profile" for super admin
    ...(!isSuperAdmin ? [{
      label: 'My Profile',
      icon: 'User',
      onClick: () => {
        navigate('/member-profile');
        setIsOpen(false);
      },
    }] : []),
    {
      label: 'Account Settings',
      icon: 'Settings',
      onClick: () => {
        navigate('/account-settings');
        setIsOpen(false);
      },
    },
    {
      label: 'Logout',
      icon: 'LogOut',
      onClick: handleLogoutClick,
      variant: 'destructive' as const,
    },
  ];

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 rounded-sm hover:bg-muted transition-interactive"
          aria-label="User menu"
        >
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center overflow-hidden">
            {userProfile?.avatar_url ? (
              <img
                src={userProfile.avatar_url}
                alt={userProfile?.full_name || 'User'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    const initials = userProfile?.full_name?.split(' ').map(n => n[0]).join('') || user?.email?.charAt(0).toUpperCase() || 'U';
                    parent.innerHTML = `<span class="text-sm font-medium text-primary-foreground">${initials}</span>`;
                  }
                }}
              />
            ) : (
              <span className="text-sm font-medium text-primary-foreground">
                {userProfile?.full_name?.split(' ').map(n => n[0]).join('') || user?.email?.charAt(0).toUpperCase() || 'U'}
              </span>
            )}
          </div>
          <div className="hidden md:block text-left">
            <div className="text-xs font-medium text-foreground">
              {userProfile?.full_name || user?.email?.split('@')[0] || 'User'}
            </div>
            <div className="text-xs text-muted-foreground">{actualRoleName}</div>
          </div>
          <Icon 
            name={isOpen ? 'ChevronUp' : 'ChevronDown'} 
            size={16} 
            className="text-muted-foreground hidden md:block"
          />
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full mt-2 w-64 bg-popover border border-border rounded-md shadow-elevation-2 z-1100">
            <div className="p-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center overflow-hidden">
                  {userProfile?.avatar_url ? (
                    <img
                      src={userProfile.avatar_url}
                      alt={userProfile?.full_name || 'User'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          const initials = userProfile?.full_name?.split(' ').map(n => n[0]).join('') || user?.email?.charAt(0).toUpperCase() || 'U';
                          parent.innerHTML = `<span class="text-base font-medium text-primary-foreground">${initials}</span>`;
                        }
                      }}
                    />
                  ) : (
                    <span className="text-base font-medium text-primary-foreground">
                      {userProfile?.full_name?.split(' ').map(n => n[0]).join('') || user?.email?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground truncate">
                    {user?.email}
                  </div>
                  <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-sm bg-primary/10 text-primary text-xs font-medium">
                    {actualRoleName}
                  </div>
                </div>
              </div>
            </div>

            <div className="py-2">
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  onClick={item.onClick}
                  className={`
                    w-full flex items-center gap-3 px-4 py-2.5
                    transition-interactive
                    ${item.variant === 'destructive' ?'text-destructive hover:bg-destructive/10' :'text-popover-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon name={item.icon} size={18} />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {showLogoutConfirm && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={(e) => {
            // Close modal when clicking backdrop
            if (e.target === e.currentTarget) {
              handleLogoutCancel();
            }
          }}
        >
          <div 
            className="bg-card rounded-md shadow-elevation-2 p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Confirm Logout
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Are you sure you want to logout? Any unsaved changes will be lost.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={handleLogoutCancel}
                    className="px-4 py-2 rounded-sm border border-border text-foreground hover:bg-muted transition-interactive"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLogoutConfirm}
                    className="px-4 py-2 rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-interactive"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default UserContextMenu;